import { Suspense } from 'react';
import { useProduct } from '@/components/product/product-context';
import { hasSelectableOptions } from '@/components/product/utils/product-context-helpers';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import offsetBlockClass from '@/utils/offsetBlockClass';
import OptionButtonGrid from '@/components/selectors/OptionButtonGrid';
import SelectedVariantDimensions from '../SelectedVariantDimensions';
import VariantSelectorModalLink from '../VariantSelectorModalLink';
import { findSelectorOption, readSelectorSettings } from '../selector-settings';
import { useOptionChoices } from '../use-option-choices';

/**
 * A variant selector rendered as a grid of buttons, one per option value.
 * The suspense fallback is a grid of placeholder tiles, which is the right
 * count on every load.
 */
export default function ButtonGrid({ block, position }) {
  const { productData } = useProduct();
  const appPathname = useAppPathname();
  const { settings, selectorSettings } = readSelectorSettings(block);
  const {
    uid,
    label,
    modal,
    microsite,
    option: optionName,
    show_offset_block_selector
  } = settings;

  const isMicrositeFeature = microsite || appPathname?.startsWith('/feature/');

  const options = productData?.shopifyProduct?.options ?? [];
  if (!hasSelectableOptions(options)) {
    return null;
  }

  const option = findSelectorOption(productData, optionName, 'button grid');
  if (!option) {
    return null;
  }

  // The selection state keys options by their lowercased name.
  const gridOptionName = option.name.toLowerCase();

  return (
    <section
      id={`${uid}_${position}`}
      data-name="button_grid-selector"
      className={offsetBlockClass(
        show_offset_block_selector,
        microsite ? 'not-last:mb-6' : 'not-last:mb-8'
      )}
    >
      <div className="flex flex-row gap-4">
        <form
          className="text-crisp-white w-full"
          data-cy="button-grid-selector-form"
        >
          <div className="flex cursor-default justify-between">
            <label htmlFor="variant-selector" className="sh12 mt-2">
              {label}
            </label>

            {microsite && (
              <Suspense fallback={null}>
                <SelectedVariantDimensions
                  showOffsetBlockSelector={show_offset_block_selector}
                />
              </Suspense>
            )}

            {selectorSettings.show_modal && (
              <VariantSelectorModalLink modal={modal} />
            )}
          </div>

          <div
            className={`variant-select-wrapper relative ${microsite ? 'mt-3' : 'mt-2'}`}
          >
            <Suspense
              fallback={
                <SkeletonGrid
                  optionValues={option.values}
                  isMicrositeFeature={isMicrositeFeature}
                />
              }
            >
              <SelectedButtonGrid
                optionName={gridOptionName}
                optionValues={option.values}
                isMicrositeFeature={isMicrositeFeature}
              />
            </Suspense>
          </div>
        </form>
      </div>
    </section>
  );
}

/**
 * Reads the selected variant and selection actions, so it needs to stay inside
 * a Suspense boundary.
 */
const SelectedButtonGrid = ({
  optionName,
  optionValues,
  isMicrositeFeature
}) => {
  const { productData } = useProduct();
  const { choices, value, onChange } = useOptionChoices(
    optionName,
    optionValues
  );

  return (
    <OptionButtonGrid
      options={choices}
      value={value}
      onChange={onChange}
      outOfStock={productData?.shopifyProduct?.outOfStock}
      className="body-r mt-2 block w-full cursor-pointer truncate focus-visible:border focus-visible:ring-4"
      data-cy="option-dropdown-size"
      isMicrositeFeature={isMicrositeFeature}
    />
  );
};

/**
 * Holds the grid's footprint in the shell: same columns, same gap, same tile
 * height as the resolved buttons, so the swap shifts nothing.
 */
const SkeletonGrid = ({ optionValues, isMicrositeFeature }) => (
  <div
    {...SKELETON_PROPS}
    className={`mt-2 grid gap-3 ${
      isMicrositeFeature
        ? 'grid-cols-3'
        : 'grid-cols-[repeat(auto-fit,minmax(100px,1fr))]'
    }`}
  >
    {optionValues.map((value) => (
      <div key={value} className="bg-parlor-green-80 min-h-12" />
    ))}
  </div>
);
